// Part 1a rows: rows for a plan that has none. Pre-1a drafts and runs stored a plan (and,
// for drafts, budgetOverrides) but no rows; legacy draft resume (rebuildLegacyReview) and
// legacy Re-run (rerunRun) rebuild rows here, routing once under the current policy, plan
// max and custom models. Permanent.
//
// Pure and deterministic: no React, no I/O. Never mutates the plan.
import { normalizeParsedWithObjectives, overrideKeyFor } from '../../controllers/planning';
import { PLAN_LIMITS } from '../../config/planLimits';
import { MAX_ROW_CANDIDATES, cloneRow } from './rowContract';
import { routeAndPrefill } from './prefillRows';
import { manualFormToRows, manualFormFromSpec } from './manualRows';

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** A legacy override as a count from 1 to MAX_ROW_CANDIDATES, or null when it is not a number. */
function overrideCount(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n)) return null;
  return Math.min(MAX_ROW_CANDIDATES, Math.max(1, n));
}

/**
 * Rows for a plan that has no rows (pre-1a drafts and pre-1a runs).
 * The first step, for both kinds, is normalizeParsedWithObjectives, as dispatch and the
 * planned-steps preview did before 1a, so an objectives-only plan routes and conditions on
 * targets.
 * - Manual plan (meta.created_by 'manual'): manualFormToRows(manualFormFromSpec(spec)),
 *   which normalizes every legacy value (D39). No router snapshot.
 * - NL plan: routeAndPrefill once; every generate row r<i+1> whose
 *   overrideKeyFor(calls[i].service, calls[i].variant) is in budgetOverrides takes that count
 *   (clamped into 1 to 1024) and loses its adjustments. Represent rows keep their seed count.
 *   Overrides are expert counts: they are not re-snapped or re-fitted here (the prepareRows
 *   backstop fits at Execute). routerSnapshot keeps the unedited prefill rows.
 * @param plan { generation_spec } (in-memory history rows carry `plan`, Firestore rows
 *   `generationSpec`; the caller passes { generation_spec: h.generationSpec })
 * @param ctx  { customModels, modelPolicy, planMax, budgetOverrides = {}, manualMattergenCustomModelId = null }
 * @returns {{ spec, rows, routerSnapshot }}  spec: the objectives-normalized spec;
 *   routerSnapshot: { rows, skipped, reconstructed: true }, or null for manual plans
 */
export function rowsFromLegacyPlan(plan, ctx = {}) {
  const c = isPlainObject(ctx) ? ctx : {};
  const normalized = normalizeParsedWithObjectives(plan);
  const spec = normalized && isPlainObject(normalized.generation_spec) ? normalized.generation_spec : {};
  const customModels = Array.isArray(c.customModels) ? c.customModels : [];
  const planMax = Number(c.planMax) || PLAN_LIMITS.pilot.maxCandidatesPerRun;

  if (spec.meta?.created_by === 'manual') {
    const form = manualFormFromSpec(spec, { mgCustomModelId: c.manualMattergenCustomModelId ?? null });
    const { rows } = manualFormToRows(form, { customModels, modelPolicy: c.modelPolicy, planMax });
    return { spec, rows, routerSnapshot: null };
  }

  const { calls, rows: snapshotRows, skipped } = routeAndPrefill(spec, { modelPolicy: c.modelPolicy, customModels, planMax });
  const overrides = isPlainObject(c.budgetOverrides) ? c.budgetOverrides : {};
  const rows = snapshotRows.map((snapshotRow) => {
    const row = cloneRow(snapshotRow);
    if (row.kind !== 'generate') return row;
    const call = calls[Number(row.id.slice(1)) - 1];
    if (!call) return row;
    const key = overrideKeyFor(call.service, call.variant);
    if (!Object.prototype.hasOwnProperty.call(overrides, key)) return row;
    const count = overrideCount(overrides[key]);
    if (count === null) return row;
    row.candidates = count;
    row.adjustments = {};
    return row;
  });
  return {
    spec,
    rows,
    routerSnapshot: { rows: snapshotRows.map((r) => cloneRow(r)), skipped, reconstructed: true },
  };
}
